import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import SEO from "../components/SEO";
import FeaturesSection from "./landing/FeaturesSection";
import HowItWorksSection from "./landing/HowItWorksSection";
import PricingSection from "./landing/PricingSection";
import CtaFinalSection from "./landing/CtaFinalSection";
import LandingFooter from "./landing/LandingFooter";
import { 
  Brain, 
  Briefcase, 
  Building2, 
  CheckCircle2, 
  Search, 
  TrendingUp, 
  ShieldCheck,
  ArrowRight,
  Sparkles,
  Clock,
  Award,
  MapPin,
  Euro,
  Calendar,
  Loader2,
  ChevronRight,
  X,
  Plus,
  Menu
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { trackCTAClick } from "../utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTORS = [
  "BTP",
  "Informatique",
  "Services",
  "Fournitures",
  "Transport",
  "Santé",
  "Environnement",
  "Communication",
  "Formation",
  "Conseil"
];

const REGIONS = [
  "Auvergne-Rhône-Alpes",
  "Bourgogne-Franche-Comté",
  "Bretagne",
  "Centre-Val de Loire",
  "Corse",
  "Grand Est",
  "Hauts-de-France",
  "Île-de-France",
  "Normandie",
  "Nouvelle-Aquitaine",
  "Occitanie",
  "Pays de la Loire",
  "Provence-Alpes-Côte d'Azur"
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Trial state
  const [showTrial, setShowTrial] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialResults, setTrialResults] = useState(null);
  const [trialForm, setTrialForm] = useState({
    sector: "",
    region: "",
    keywords: []
  });
  const [newKeyword, setNewKeyword] = useState("");

  const handleTrialSubmit = async () => {
    if (!trialForm.sector || !trialForm.region) {
      toast.error("Veuillez sélectionner un secteur et une région");
      return;
    }
    
    setTrialLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/trial/recommendations`, {
        sector: trialForm.sector,
        region: trialForm.region,
        keywords: trialForm.keywords
      });
      setTrialResults(response.data);
      toast.success("Voici vos 3 recommandations !");
    } catch (error) {
      toast.error("Erreur lors de l'analyse. Réessayez.");
    } finally {
      setTrialLoading(false);
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !trialForm.keywords.includes(newKeyword.trim())) {
      setTrialForm(prev => ({
        ...prev,
        keywords: [...prev.keywords, newKeyword.trim()]
      }));
      setNewKeyword("");
    }
  };

  const removeKeyword = (keyword) => {
    setTrialForm(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600 bg-green-100";
    if (score >= 60) return "text-yellow-600 bg-yellow-100";
    return "text-orange-600 bg-orange-100";
  };

  const stats = [
    { value: "20+", label: "Sources officielles" },
    { value: "600+", label: "Marchés en cours" },
    { value: "IA", label: "GPT-5.2" },
    { value: "0", label: "Doublons" }
  ];


  return (
    <>
      <SEO 
        title="Trouvez les appels d'offres pertinents et obtenez une synthèse du DCE"
        description="Veille marchés publics automatisée par IA. Analyse BOAMP, TED, DECP. Synthèse intelligente des DCE en 30 secondes. Trouvez les marchés adaptés à votre entreprise."
        path="/"
      />
      <div className="min-h-screen bg-white">
        {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link to="/" className="flex items-center">
              <img 
                src="/logo-dce-express.png" 
                alt="DCE Express" 
                className="h-16 md:h-20 w-auto"
              />
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#demo" className="text-slate-600 hover:text-violet-700 transition-colors">Démo</a>
              <a href="#features" className="text-slate-600 hover:text-violet-700 transition-colors">Fonctionnalités</a>
              <a href="#pricing" className="text-slate-600 hover:text-violet-700 transition-colors">Tarifs</a>
              <Link to="/analyse-dce" className="text-slate-600 hover:text-violet-700 transition-colors">Synthèse IA</Link>
              <Link to="/guide-appels-offres" className="text-slate-600 hover:text-violet-700 transition-colors">Guide</Link>
              <Link to="/blog" className="text-slate-600 hover:text-violet-700 transition-colors">Blog</Link>
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-6"
                  data-testid="nav-dashboard-btn"
                >
                  Mon Dashboard
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate("/login")}
                    className="text-slate-700"
                    data-testid="nav-login-btn"
                  >
                    Connexion
                  </Button>
                  <Button 
                    onClick={() => { trackCTAClick("signup", "nav_top"); navigate("/register"); }}
                    className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-6"
                    data-testid="nav-register-btn"
                  >
                    S'inscrire
                  </Button>
                </div>
              )}
            </div>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:hidden py-4 border-t border-slate-200"
            >
              <div className="flex flex-col gap-2">
                <a href="#demo" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Démo</a>
                <a href="#features" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Fonctionnalités</a>
                <a href="#pricing" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Tarifs</a>
                <Link to="/analyse-dce" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Synthèse IA</Link>
                <Link to="/analyse-gratuite" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Analyse Gratuite</Link>
                <Link to="/guide-appels-offres" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Guide</Link>
                <Link to="/blog" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/contact" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
                <Link to="/mentions-legales" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Mentions légales</Link>
                <div className="border-t border-slate-200 mt-2 pt-4 px-4">
                  {user ? (
                    <Button 
                      onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }}
                      className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-full"
                    >
                      Mon Dashboard
                    </Button>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}
                        className="w-full"
                      >
                        Connexion
                      </Button>
                      <Button 
                        onClick={() => { trackCTAClick("signup", "nav_mobile"); navigate("/register"); setMobileMenuOpen(false); }}
                        className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-full"
                      >
                        S'inscrire gratuitement
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section - Refonte premium 2026 */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950"></div>
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, rgba(139, 92, 246, 0.25) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(99, 102, 241, 0.2) 0%, transparent 50%)"
        }}></div>
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")"
        }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* LEFT - Text content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm mb-8">
                <Sparkles className="w-4 h-4 text-violet-300" />
                <span>Veille IA pour les marchés publics</span>
              </div>

              <h1
                className="font-display font-bold text-white tracking-tight mb-8"
                style={{
                  fontSize: "clamp(2.25rem, 5vw, 3.75rem)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em"
                }}
              >
                Trouvez les appels d'offres
                <br className="hidden sm:block" />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-300 via-fuchsia-300 to-indigo-300">
                  qui vous correspondent
                </span>
                <br className="hidden sm:block" />
                <span className="text-slate-200">en quelques minutes.</span>
              </h1>

              <p
                className="text-slate-300 max-w-xl mx-auto lg:mx-0 mb-10"
                style={{ fontSize: "1.125rem", lineHeight: 1.5 }}
              >
                Notre intelligence artificielle analyse <span className="text-white font-medium">BOAMP, DECP et TED Europa</span> et vous recommande les meilleurs appels d'offres adaptés à votre entreprise. Synthèse d'un DCE complet en moins de 30 secondes.
              </p>

              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-12">
                <Button
                  size="lg"
                  onClick={() => { trackCTAClick("trial_free_15d", "hero"); setShowTrial(true); }}
                  className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8 py-7 text-base font-semibold shadow-2xl shadow-violet-900/40 hover:shadow-violet-900/60 hover:-translate-y-0.5 transition-all w-full sm:w-auto"
                  data-testid="try-free-btn"
                  style={{ minHeight: "56px" }}
                >
                  <Sparkles className="mr-2 w-5 h-5" />
                  Essai gratuit 15 jours
                </Button>
                {!user && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => { trackCTAClick("signup", "hero"); navigate("/register"); }}
                    className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-7 text-base font-medium backdrop-blur-md w-full sm:w-auto"
                    data-testid="hero-register-btn"
                    style={{ minHeight: "56px" }}
                  >
                    Créer un compte
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                )}
              </div>

              {/* Sources officielles - badges */}
              <div className="border-t border-white/10 pt-8">
                <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
                  Sources officielles intégrées
                </p>
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3">
                  {["BOAMP", "DECP", "TED Europa", "République Française", "SNCF", "ADP"].map((src) => (
                    <div
                      key={src}
                      className="text-slate-300 text-sm font-medium tracking-wide"
                    >
                      {src}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* RIGHT - Dashboard mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5 relative"
            >
              {/* Glow behind */}
              <div className="absolute -inset-8 bg-gradient-to-br from-violet-500/30 via-fuchsia-500/20 to-indigo-500/30 blur-3xl rounded-full"></div>

              {/* Main mockup card */}
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-white/10">
                {/* Mockup header */}
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-white/40"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-white/40"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-white/40"></div>
                  </div>
                  <div className="text-white text-xs font-medium tracking-wide">
                    dce-express.fr / Tableau de bord
                  </div>
                  <div className="w-12"></div>
                </div>

                {/* Mockup body */}
                <div className="p-5 space-y-4 bg-slate-50">
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl p-3 border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Marchés actifs</div>
                      <div className="text-xl font-bold text-slate-900">2 153</div>
                      <div className="text-[10px] text-emerald-600 font-medium">+12% cette semaine</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Match IA</div>
                      <div className="text-xl font-bold text-violet-600">94%</div>
                      <div className="text-[10px] text-slate-500 font-medium">Score élevé</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Synthèse IA</div>
                      <div className="text-xl font-bold text-slate-900">30s</div>
                      <div className="text-[10px] text-slate-500 font-medium">Par document</div>
                    </div>
                  </div>

                  {/* Recommended tenders */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-violet-600" />
                        <span className="text-sm font-semibold text-slate-900">Recommandations du jour</span>
                      </div>
                      <span className="text-[10px] text-violet-600 font-semibold">IA</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {[
                        { title: "Maintenance informatique • Ville de Lyon", match: "96%", amount: "480 K€" },
                        { title: "Travaux réfection toiture • Hôpital Paris", match: "89%", amount: "320 K€" },
                        { title: "Audit cybersécurité • Région PACA", match: "84%", amount: "150 K€" }
                      ].map((t, i) => (
                        <div key={`item-${i}`} className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-slate-900 truncate">{t.title}</div>
                            <div className="text-[10px] text-slate-500">{t.amount}</div>
                          </div>
                          <span className="ml-3 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">
                            {t.match}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating "Nouveau" card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-2xl px-4 py-3 border border-violet-100 hidden md:flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-900">+247 nouveaux marchés</div>
                  <div className="text-[10px] text-slate-500">Aujourd'hui · BOAMP</div>
                </div>
              </motion.div>

              {/* Floating "GPT-5.2" badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.9 }}
                className="absolute -top-4 -right-4 bg-gradient-to-br from-violet-600 to-indigo-700 rounded-xl shadow-2xl px-4 py-2.5 hidden md:flex items-center gap-2"
              >
                <Brain className="w-4 h-4 text-white" />
                <span className="text-xs font-bold text-white tracking-wide">GPT-5.2</span>
              </motion.div>
            </motion.div>
          </div>

          {/* Stats - bottom of hero */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20 md:mt-28 pt-12 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <div key={`item-${index}`} className="text-center lg:text-left">
                <div className="text-3xl md:text-4xl font-display font-bold text-white mb-1 tracking-tight">{stat.value}</div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trial Modal */}
      {showTrial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
          >
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-xl font-bold text-slate-900">
                      Essai gratuit
                    </h2>
                    <p className="text-sm text-slate-500">Sans inscription, découvrez vos 3 marchés</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setShowTrial(false); setTrialResults(null); }}
                  data-testid="close-trial-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="p-6">
              {!trialResults ? (
                <div className="space-y-6">
                  <p className="text-slate-600">
                    Renseignez quelques informations sur votre entreprise pour recevoir des recommandations personnalisées.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Secteur d'activité *</label>
                      <Select
                        value={trialForm.sector}
                        onValueChange={(value) => setTrialForm({ ...trialForm, sector: value })}
                      >
                        <SelectTrigger data-testid="trial-sector-select">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTORS.map(sector => (
                            <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Région *</label>
                      <Select
                        value={trialForm.region}
                        onValueChange={(value) => setTrialForm({ ...trialForm, region: value })}
                      >
                        <SelectTrigger data-testid="trial-region-select">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {REGIONS.map(region => (
                            <SelectItem key={region} value={region}>{region}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Mots-clés (optionnel)</label>
                    <div className="flex gap-2">
                      <Input
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        placeholder="Ex: cybersécurité, maintenance, logiciel..."
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                        data-testid="trial-keyword-input"
                      />
                      <Button type="button" onClick={addKeyword} variant="outline">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {trialForm.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {trialForm.keywords.map(keyword => (
                          <Badge
                            key={keyword}
                            variant="secondary"
                            className="bg-slate-100 text-slate-700 pr-1"
                          >
                            {keyword}
                            <button
                              type="button"
                              onClick={() => removeKeyword(keyword)}
                              className="ml-1 p-1 hover:bg-slate-200 rounded"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleTrialSubmit}
                    disabled={trialLoading || !trialForm.sector || !trialForm.region}
                    className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-full py-6"
                    data-testid="trial-submit-btn"
                  >
                    {trialLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Analyse en cours par l'IA...
                      </>
                    ) : (
                      <>
                        <Brain className="w-5 h-5 mr-2" />
                        Obtenir mes 3 recommandations
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Analyse terminée !</span>
                  </div>

                  <div className="space-y-4">
                    {trialResults.map((rec, index) => (
                      <Card 
                        key={rec.tender.id}
                        className="border-l-4 border-l-violet-500"
                        data-testid={`trial-result-${index}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={`${getScoreColor(rec.match_score)} font-semibold`}>
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  {rec.match_score}%
                                </Badge>
                                <Badge variant="outline" className="text-slate-600">
                                  {rec.tender.sector}
                                </Badge>
                                <Badge variant="outline" className={`text-xs ${
                                  rec.tender.source === "BOAMP" ? "bg-blue-50 text-blue-600 border-blue-200" : 
                                  rec.tender.source === "DECP" ? "bg-green-50 text-green-600 border-green-200" :
                                  rec.tender.source === "SNCF" ? "bg-red-50 text-red-600 border-red-200" :
                                  rec.tender.source === "ADP" ? "bg-purple-50 text-purple-600 border-purple-200" :
                                  rec.tender.source === "RATP" ? "bg-teal-50 text-teal-600 border-teal-200" :
                                  rec.tender.source === "MSEC" ? "bg-orange-50 text-orange-600 border-orange-200" :
                                  rec.tender.source === "DEMATIS" ? "bg-pink-50 text-pink-600 border-pink-200" :
                                  "bg-slate-50"
                                }`}>
                                  {rec.tender.source}
                                </Badge>
                              </div>
                              
                              <h3 className="font-display text-base font-semibold text-slate-900 mb-2 line-clamp-2">
                                {rec.tender.title}
                              </h3>
                              
                              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-3">
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {rec.tender.buyer}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {rec.tender.region}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(rec.tender.deadline).toLocaleDateString('fr-FR')}
                                </span>
                              </div>

                              <div className="bg-slate-50 rounded-lg p-2">
                                <p className="text-xs font-medium text-slate-500 mb-1">Pourquoi ce marché ?</p>
                                <ul className="space-y-0.5">
                                  {rec.match_reasons.slice(0, 2).map((reason, i) => (
                                    <li key={`item-${i}`} className="flex items-start gap-1 text-xs text-slate-600">
                                      <ChevronRight className="w-3 h-3 text-violet-500 flex-shrink-0 mt-0.5" />
                                      <span className="line-clamp-1">{reason}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-4 border border-violet-100">
                    <h4 className="font-semibold text-slate-900 mb-2">
                      Vous voulez plus de recommandations ?
                    </h4>
                    <p className="text-sm text-slate-600 mb-4">
                      Créez un compte gratuit pour sauvegarder vos favoris et recevoir des alertes personnalisées.
                    </p>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => { trackCTAClick("signup", "post_trial"); setShowTrial(false); navigate("/register"); }}
                        className="bg-violet-700 hover:bg-violet-800 text-white rounded-full"
                        data-testid="trial-register-btn"
                      >
                        Créer un compte gratuit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { setShowTrial(false); setTrialResults(null); }}
                      >
                        Fermer
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <FeaturesSection />
      <HowItWorksSection onTryFree={() => setShowTrial(true)} />

      <PricingSection onSubscribe={() => setShowTrial(true)} />

      <CtaFinalSection onTryFree={() => setShowTrial(true)} />
      <LandingFooter />
    </div>
    </>
  );
};

export default LandingPage;
